// First-party A/B testing on the web (API_CONTRACT §6n): the small part
// that ships in main.js. The network, unit id and event code is the lazy
// chunk utils/experimentsNet.js, loaded after `load` + idle (ExperimentsBoot).
//
// Rules (owner, 5 Oct):
// - Assignments are functional (which version of a page to show), not
//   tracking: fetched once per session after `load` + idle, so they never
//   compete with the largest paint. Nothing is stored on the server for them.
// - No layout shift: `useVariant` reads the assignment ONCE, when the
//   component mounts. A component that mounted before the assignments
//   arrived keeps the control (today's UI) for its lifetime and logs no
//   exposure; the variant shows from the next page / mount on. The first
//   page of a first visit is therefore always the control.
// - Exposure is logged once per experiment per session, when the component
//   that reads the payload key actually renders (both arms).
// - Events (exposure, add_to_bag, checkout_start) and the order header are
//   sent ONLY with analytics consent (utils/consent.js). Without it the unit
//   id lives in sessionStorage only and nothing is sent.
// - Order revenue is never sent by the browser: the server records it from
//   POST /orders (X-Experiment-Unit header, experimentsNet.experimentOrderHeaders).
import { useEffect, useRef } from 'react';
import { getConsent } from './consent';

export const ASSIGN_KEY = 'anum_exp_assign'; // sessionStorage: this session's assignments

const readSession = () => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ASSIGN_KEY) || 'null');
    return parsed && typeof parsed.experiments === 'object' && parsed.experiments ? parsed : null;
  } catch { return null; }
};

let state = typeof window === 'undefined' ? null : readSession();

/** The assignments in force: {auth, experiments: {key: {variant, payload, keys}}} or null. */
export const currentAssignments = () => state;
/** Set by experimentsNet after a fetch (also stored in sessionStorage there). */
export const setAssignments = (next) => { state = next; };
/** Test hook: re-read the session copy. */
export const reloadAssignmentsForTests = () => { state = readSession(); };

const hasExperiments = () => Boolean(state && Object.keys(state.experiments || {}).length);
const net = () => import('./experimentsNet');

/** Fetch this session's assignments (once per sign-in state). Never throws. */
export const ensureAssignments = (token) => net().then((m) => m.ensureAssignments(token)).catch(() => null);

/**
 * What the assignments say about one payload key: {value, has, experiments}
 * (experiments = running experiments touching the key, for the exposure).
 * The server never runs two experiments on the same key and surface.
 */
export const lookupPayload = (payloadKey, assignments = state) => {
  const out = { value: undefined, has: false, experiments: [] };
  const exps = assignments?.experiments || {};
  Object.keys(exps).sort().forEach((key) => {
    const entry = exps[key] || {};
    const keys = Array.isArray(entry.keys) ? entry.keys : Object.keys(entry.payload || {});
    if (!keys.includes(payloadKey)) return;
    out.experiments.push(key);
    if (!out.has && entry.payload && Object.prototype.hasOwnProperty.call(entry.payload, payloadKey)) {
      out.value = entry.payload[payloadKey];
      out.has = true;
    }
  });
  return out;
};

/** A conversion step (`add_to_bag` | `checkout_start`); the server credits
 * the experiments this unit was exposed to. No-op without assignments or
 * analytics consent (returns false). */
export const trackExperiment = (type) => {
  if (!hasExperiments() || getConsent().analytics !== true) return false;
  net().then((m) => m.trackExperiment(type)).catch(() => {});
  return true;
};

/**
 * The value of an experiment payload key for this visitor, or `fallback`
 * (= the control, today's UI). Read once at mount (no layout shift); logs
 * the exposure once the component has rendered while `expose` is true.
 * `resetKey`: read again when it changes (a page that swaps its whole
 * content in place, e.g. the product page moving to another product).
 *
 *   const showBar = useVariant('pdp_sticky_bar', true, { expose: isPhone, resetKey: id });
 */
export const useVariant = (payloadKey, fallback, { expose = true, resetKey } = {}) => {
  const snapshot = useRef(null);
  if (snapshot.current === null || snapshot.current.resetKey !== resetKey) {
    snapshot.current = { resetKey, hit: lookupPayload(payloadKey) };
  }
  const { hit } = snapshot.current;
  useEffect(() => {
    if (!expose || !hit.experiments.length || getConsent().analytics !== true) return;
    net().then((m) => hit.experiments.forEach((key) => m.markExposed(key))).catch(() => {});
  }, [expose, hit]);
  return hit.has ? hit.value : fallback;
};
